import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, Signal, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { environment } from '@environments/environment';
import { BRAND_NAMES, MenuItem } from '@lib/rs-ngx';
import { UserService } from '@core/services/user.service';
import { Title } from '@angular/platform-browser';
import { User } from '@core/models/user';
import { toSignal } from '@angular/core/rxjs-interop';
import { FeatureFlagService } from '@core/feature-flag/feature-flag.service';
import { FeatureFlag } from '@core/feature-flag/feature-flag.model';
import { map, switchMap } from 'rxjs';
import { isNil, isEmpty } from 'lodash';
import { RsTopBarMenuComponent } from '@rs-shared-lib/components/topbar/rs-top-bar-menu.component';
import { RsLoaderComponent } from '@rs-shared-lib/components/rs-loader/components/rs-loader.component';
import { RsFooterComponent } from '@rs-shared-lib/components/footer/rs-footer.component';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrl: 'app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    RouterOutlet,
    RsFooterComponent,
    RsLoaderComponent,
    RsTopBarMenuComponent,
  ],
})
export class AppComponent {
  protected readonly menuItems: Signal<MenuItem[]>;
  protected readonly appVersion = environment.version!;

  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly userService = inject(UserService);

  public constructor() {
    const titleService = inject(Title);

    this.menuItems = this.createMenuItems();

    titleService.setTitle(BRAND_NAMES['MRT']);
  }

  public clearCurrentUserOnLogout(): void {
    this.userService.setCurrentUser(null);
  }

  public topBarRouteChange(path: string): void {
    if (path === '/rs-number') {
      this.clearCurrentUserOnLogout();
    }
  }

  private createMenuItems(): Signal<MenuItem[]> {
    // Fetch fresh flags per user (bypassing the shared/replayed stream, which
    // can still hold the previous user's flags while the new ones are loading).
    // With switchMap, the menu keeps showing the previous user's full menu
    // until the new one is entirely ready, instead of updating item-by-item
    // (e.g. "Switch user" appearing alone before the rest of the buttons).
    const menuItems$ = this.userService.getCurrentUser$().pipe(
      switchMap(user => this.featureFlagService.getFeatureFlagsForUser(user).pipe(
        map(featureFlags => this.initMenus(user, featureFlags)),
      )),
    );

    return toSignal(menuItems$, { initialValue: [] });
  }

  private initMenus(user: User | null | undefined, featureFlags: FeatureFlag[]): MenuItem[] {
    return [
      ...this.createMenuItemsForUserRole(featureFlags),
      {
        url: 'rs-number',
        id: 'rs-number',
        label: 'SWITCH_ACCOUNT',
        display: !isNil(user),
        labelSuffix: '(' + user?.loginString + ')',
        title: user?.companyName,
      },
      {
        url: 'old-ui',
        id: 'old-ui',
        display: !isEmpty(environment.legacyUiUrl) && featureFlags.includes('new-ui.show-button'),
        label: 'RETURN_TO_OLD_UI',
        labelParams: { appName: BRAND_NAMES['MRT'] }
      },
    ];
  }

  private createMenuItemsForUserRole(featureFlags: FeatureFlag[]): MenuItem[] {
    if (this.userService.isAdminUser()) {
      return this.createMenusItemsForAdmin(featureFlags);
    }
    if (this.userService.hasAnyLeasingCompanyUserRole()) {
      return this.createMenuItemsForLeasingCompany(featureFlags);
    }
    if (this.userService.isSupplierUser()) {
      return this.createMenuItemsForSupplier(featureFlags);
    }
    return [];
  }

  private createMenusItemsForAdmin(featureFlags: FeatureFlag[]): MenuItem[] {
    if (!featureFlags.includes('new-ui.scope.admin')) {
      return [];
    }

    return [
      {
        label: 'MANAGEMENT',
        id: 'management',
        display: this.userService.hasAccessToManagement(),
        subMenuItems: [
          {
            id: 'brand-activity-mappings',
            url: 'admin/brand-activity-mappings',
            label: 'BRAND_ACTIVITY_MAPPINGS',
            display: this.userService.isAdminUser(),
          },
          {
            url: 'admin/activities/definition',
            id: 'activities',
            label: 'ACTIVITIES',
            display: this.userService.isAdminUser(),
          },
          {
            url: 'admin/activity-groups',
            id: 'activityGroups',
            label: 'ACTIVITY_GROUPS',
            display: this.userService.isAdminUser(),
          },
          {
            url: 'admin/vehicle-types',
            id: 'admin-vehicle-types',
            label: 'VEHICLE_TYPES',
            display: this.userService.isAdminUser(),
          },
          {
            url: 'admin/disapproval-reasons',
            id: 'admin-disapproval-reasons',
            label: 'DISAPPROVAL_REASONS',
            display: this.userService.isAdminUser(),
          },
          {
            url: 'admin/rental-classes',
            id: 'rentalClasses',
            label: 'RENTAL_CLASSES',
            display: this.userService.isAdminUser(),
          },
          {
            url: 'admin/warranties',
            id: 'warranties',
            label: 'WARRANTIES',
            display: this.userService.isAdminUser(),
          },
          {
            url: 'admin/fleet-owners',
            id: 'fleetOwners',
            label: 'FLEET_OWNERS',
            display: this.userService.isAdminUser(),
          },
          {
            id: 'admin-suppliers',
            url: 'admin/suppliers',
            label: 'SUPPLIERS',
            display: this.userService.isAdminUser(),
          },
          {
            id: 'tyres',
            url: 'admin/tyres',
            label: 'TYRES',
            display: this.userService.isAdminUser(),
          },
          {
            id: 'vehicles',
            url: 'admin/vehicles',
            label: 'VEHICLES',
            display: this.userService.isAdminUser(),
          },
          {
            id: 'admin-work-orders',
            url: 'admin/work-orders',
            label: 'WORK_ORDERS',
            display: this.userService.isAdminUser(),
          },
        ],
      },
      {
        url: 'admin/users',
        label: 'USERS',
        id: 'admin-users',
        display: this.userService.isAdminUser(),
        subMenuItems: [
          {
            url: 'admin/users',
            label: 'USERS',
            id: 'users',
            display: this.userService.isAdminUser(),
          },
        ],
      },
    ];
  }

  private createMenuItemsForLeasingCompany(featureFlags: FeatureFlag[]): MenuItem[] {
    if (!featureFlags.includes('new-ui.scope.leasing-company')) {
      return [];
    }

    const hasLCWithAuthRole = this.userService.hasLeasingCompanyManagerOrLeasingCompanyWithAuthorizationUserRole();
    return [
      {
        url: 'leasing-company/work-orders',
        id: 'work-orders',
        label: 'WORK_ORDERS',
        display: this.userService.hasAnyLeasingCompanyUserRole() && featureFlags.includes('leasing-company.work-orders'),
      },
      {
        label: 'ADMIN',
        id: 'admin',
        display: hasLCWithAuthRole,
        subMenuItems: [
          {
            url: 'leasing-company/administration',
            id: 'administration',
            label: 'ADMINISTRATION',
            display: this.userService.hasAnyLeasingCompanyUserRole(),
          },
          {
            url: 'leasing-company/user-authorizations',
            id: 'user-authorizations',
            label: 'USER_AUTHORIZATIONS',
            display: this.userService.hasLeasingCompanyManagerUserRole() && featureFlags.includes('leasing-company.user-authorizations'),
          },
          {
            url: 'leasing-company/data-mapping',
            id: 'data-mapping',
            label: 'DATA_MAPPING',
            display: hasLCWithAuthRole && featureFlags.includes('leasing-company.data-mapping'),
          },
          {
            url: 'leasing-company/data-import',
            id: 'data-import',
            label: 'DATA_IMPORT',
            display: this.userService.hasLeasingCompanyManagerOrLeasingCompanyWithAuthorizationUserRole() &&
              featureFlags.includes('leasing-company.data-import'),
          },
        ],
      },
      {
        label: 'MANAGEMENT',
        id: 'management',
        display: this.userService.hasAccessToManagement(),
        subMenuItems: [
          {
            url: 'leasing-company/leasing-contracts',
            id: 'leasing-contracts',
            label: 'LEASING_CONTRACTS',
            display: hasLCWithAuthRole && featureFlags.includes('leasing-company.leasing-contracts'),
          },
          {
            url: 'leasing-company/supplier-groups',
            id: 'supplier-groups',
            label: 'SUPPLIER_GROUPS',
            display: hasLCWithAuthRole && featureFlags.includes('leasing-company.supplier-groups'),
          },
          {
            url: 'leasing-company/suppliers',
            id: 'suppliers',
            label: 'SUPPLIERS',
            display: hasLCWithAuthRole && featureFlags.includes('leasing-company.suppliers'),
          },
          {
            url: 'leasing-company/leasing-company-thresholds',
            id: 'leasing-company-thresholds',
            label: 'LEASING_COMPANY_THRESHOLDS',
            display: hasLCWithAuthRole && featureFlags.includes('leasing-company.leasing-company-thresholds'),
          },
          {
            url: 'leasing-company/maintenance-intervals',
            id: 'main-intervals',
            label: 'MAIN_INTERVALS',
            display: hasLCWithAuthRole && featureFlags.includes('leasing-company.maintenance-intervals'),
          },
          {
            url: 'leasing-company/activity-group-reductions',
            id: 'activity-group-reductions',
            label: 'ACTIVITY_GROUP_REDUCTIONS',
            display: hasLCWithAuthRole && featureFlags.includes('leasing-company.activity-group-reductions'),
          },
          {
            url: 'leasing-company/packs',
            id: 'packs',
            label: 'PACKS',
            display: hasLCWithAuthRole && featureFlags.includes('leasing-company.packs'),
          },
          {
            url: 'leasing-company/custom-comments',
            id: 'customComments',
            label: 'CUSTOM_COMMENTS',
            display: hasLCWithAuthRole,
          },
          {
            id: 'vehicle-types',
            url: 'leasing-company/vehicle-types',
            label: 'VEHICLE_TYPES',
            display: hasLCWithAuthRole,
          },
          {
            id: 'disapproval-reasons',
            url: 'leasing-company/disapproval-reasons',
            label: 'DISAPPROVAL_REASONS',
            display: hasLCWithAuthRole,
          },
          {
            id: 'disapproval-reason-thresholds',
            url: 'leasing-company/disapproval-reason-thresholds',
            label: 'DISAPPROVAL_REASON_THRESHOLDS',
            display: hasLCWithAuthRole,
          },
          {
            id: 'preferred-tyre-brands',
            url: 'leasing-company/preferred-tyre-brands',
            label: 'PREFERRED_TYRE_BRANDS',
            display: hasLCWithAuthRole && featureFlags.includes('leasing-company.preferred-tyre-brands'),
          },
          {
            id: 'rental-classes',
            url: 'leasing-company/rental-classes',
            label: 'RENTAL_CLASSES',
            display: hasLCWithAuthRole,
          },
          {
            id: 'brand-thresholds',
            url: 'leasing-company/brand-thresholds',
            label: 'BRAND_THRESHOLDS',
            display: hasLCWithAuthRole,
          },
          {
            id: 'tyre-maintenances',
            url: 'leasing-company/tyre-maintenances',
            label: 'TYRE_MAINTENANCES',
            display: hasLCWithAuthRole,
          },
          {
            id: 'tyre-brand-discounts',
            url: 'leasing-company/tyre-brand-discounts',
            label: 'GLOBAL_TYRE_BRAND_DISCOUNTS',
            display: hasLCWithAuthRole && featureFlags.includes('leasing-company.tyre-brand-discounts'),
          },
          {
            id: 'coulance',
            url: 'leasing-company/coulance',
            label: 'COULANCE',
            display: hasLCWithAuthRole,
          },
          {
            id: 'leasing-contract-groups',
            url: 'leasing-company/leasing-contract-groups',
            label: 'LEASING_CONTRACT_GROUPS',
            display: hasLCWithAuthRole,
          },
          {
            id: 'cost-centre-configuration',
            url: 'leasing-company/cost-centre-configuration',
            label: 'COST_CENTRES',
            display: hasLCWithAuthRole,
          },
          {
            id: 'activity-settings',
            url: 'leasing-company/activity-settings',
            label: 'ACTIVITY_SETTINGS',
            display: hasLCWithAuthRole,
          },
        ],
      },
    ];
  }

  private createMenuItemsForSupplier(featureFlags: FeatureFlag[]): MenuItem[] {
    if (!featureFlags.includes('new-ui.scope.supplier')) {
      return [];
    }

    return [
      {
        url: 'supplier/work-orders',
        id: 'work-orders',
        label: 'WORK_ORDERS',
        display: this.userService.isSupplierUser(),
      },
      {
        url: 'supplier/vehicle-history',
        id: 'vehicle-history',
        label: 'VEHICLE_HISTORY',
        display: this.userService.isSupplierUser(),
      },
    ];
  }
}
